import * as XLSX from "xlsx";

import { supabase } from "~/integrations/supabase/client";

// Working-sheet headers. Reference columns first (pre-filled, don't edit), then the
// input columns (blank, self-documenting). The parser matches these by keyword, so
// the long human-readable labels are safe to keep.
const HEADERS = [
  "Tipo",
  "Código",
  "Ítem",
  "Unidad",
  "Bodega/Planta (Producto: B1/B2/NC · Ingrediente: P1/P2)",
  "Cantidad a ajustar (+ agrega / − quita · vacío = sin cambio)",
  "Costo unitario (opcional · vacío = usa promedio)",
  "Lote (solo decrementos de producto)",
  "Notas (opcional)",
];

interface CatalogRow {
  tipo: string;
  codigo: string;
  item: string;
  unidad: string;
}

/**
 * Download the "Ajuste Masivo" catálogo template: one row per live item
 * (productos + aceites + materia prima) with the reference columns pre-filled and
 * the input columns blank, plus an Instrucciones sheet. Deliberately carries NO
 * stock or cost figures — the user fills only the quantities they want to adjust.
 * Modeled on stockConsultationExportService.ts.
 */
export async function downloadBulkAdjustmentCatalog(): Promise<void> {
  const [prods, oils, mats] = await Promise.all([
    supabase
      .from("products")
      .select("sku, short_name, base_measurement_unit")
      .is("deleted_at", null),
    supabase.from("oil_types").select("code, name"),
    supabase.from("raw_materials").select("code, name, unit"),
  ]);
  if (prods.error) throw prods.error;
  if (oils.error) throw oils.error;
  if (mats.error) throw mats.error;

  const rows: CatalogRow[] = [];
  for (const p of prods.data ?? []) {
    if (!p.sku) continue;
    rows.push({
      tipo: "Producto",
      codigo: p.sku,
      item: p.short_name ?? "",
      unidad: p.base_measurement_unit || "caja",
    });
  }
  for (const o of oils.data ?? []) {
    if (!o.code) continue;
    rows.push({ tipo: "Aceite", codigo: o.code, item: o.name ?? "", unidad: "kg" });
  }
  for (const m of mats.data ?? []) {
    if (!m.code) continue;
    rows.push({
      tipo: "Materia Prima",
      codigo: m.code,
      item: m.name ?? "",
      unidad: m.unit || "UNI",
    });
  }
  rows.sort(
    (a, b) => a.tipo.localeCompare(b.tipo) || a.codigo.localeCompare(b.codigo)
  );

  // "Ajustes" sheet: header + one row per item, input columns left blank.
  const aoa: (string | number)[][] = [HEADERS];
  for (const r of rows) {
    aoa.push([r.tipo, r.codigo, r.item, r.unidad, "", "", "", "", ""]);
  }
  const wsAjustes = XLSX.utils.aoa_to_sheet(aoa);
  wsAjustes["!cols"] = [
    { wch: 14 },
    { wch: 12 },
    { wch: 42 },
    { wch: 10 },
    { wch: 52 },
    { wch: 52 },
    { wch: 40 },
    { wch: 28 },
    { wch: 24 },
  ];

  const instrucciones: string[][] = [
    ["Ajuste Masivo de Stock — Instrucciones"],
    [""],
    [
      "1) Escribe una Cantidad solo en las filas que quieras ajustar. Las filas en blanco se ignoran.",
    ],
    ["2) Cantidad es un DELTA: +10 agrega 10, −10 quita 10. NO es el stock final."],
    ["3) No borres ni edites las columnas Tipo, Código ni Ítem (son de referencia)."],
    ["4) Bodega/Planta — Producto: B1, B2 o NC. Aceite/Materia Prima: P1 o P2."],
    ["5) Costo unitario: déjalo vacío para NO alterar el costo promedio."],
    ["6) El motivo se elige en la aplicación y aplica a todo el archivo."],
    [
      "7) Nada se ajusta hasta subir el archivo, revisar la vista previa y confirmar.",
    ],
  ];
  const wsInstr = XLSX.utils.aoa_to_sheet(instrucciones);
  wsInstr["!cols"] = [{ wch: 96 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsAjustes, "Ajustes");
  XLSX.utils.book_append_sheet(wb, wsInstr, "Instrucciones");
  XLSX.writeFile(wb, "ajuste_masivo_catalogo.xlsx");
}
